import { useMutation } from '@tanstack/react-query';
import { PrimaryActionButton, StonePanel } from '@aztec-realms/ui';
import { useId, useState, type FormEvent } from 'react';

import { appBuildLabel } from './app-build';
import { loginPlayer } from './data/player-auth';

interface PlayerLoginScreenProps {
  onAuthenticated: () => Promise<unknown> | unknown;
  authenticate?: typeof loginPlayer;
  initialEmail?: string;
  notice?: string;
}

export function PlayerLoginScreen({
  onAuthenticated,
  authenticate = loginPlayer,
  initialEmail = '',
  notice,
}: PlayerLoginScreenProps) {
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const login = useMutation({
    mutationFn: () => authenticate(email.trim(), password),
    onSuccess: async () => {
      setPassword('');
      await onAuthenticated();
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    login.mutate();
  };

  const clearError = () => {
    if (login.error) login.reset();
  };

  return (
    <main
      aria-labelledby="player-login-title"
      className="player-login-shell"
      data-visual-ready="true"
      data-visual-state="player-login"
    >
      <div aria-hidden="true" className="player-login-atmosphere">
        <span className="player-login-atmosphere__sun" />
        <span className="player-login-atmosphere__water" />
      </div>
      <section aria-label="Aztec Realms" className="player-login-story">
        <div className="player-login-brand">
          <span aria-hidden="true" className="player-login-brand__mark">
            <svg focusable="false" viewBox="0 0 64 64">
              <circle cx="32" cy="17" r="8" />
              <path d="M32 3v5M20 7l5 6m19-6-5 6M14 17h7m22 0h7" />
              <path d="M12 55h40M18 54V43h28v11M23 43V32h18v11M28 32V22h8v10" />
              <path d="m23 54 9-12 9 12" />
            </svg>
          </span>
          <span>
            <strong>Aztec Realms</strong>
            <small>Runes &amp; Tribes</small>
          </span>
        </div>
        <div className="player-login-story__copy">
          <span className="player-login-eyebrow">A living world remembers</span>
          <h2>Your realm is still becoming.</h2>
          <p>
            Return to the waterways, restore forgotten signs and watch every discovery take physical
            form.
          </p>
          <ol aria-label="Core game loop" className="player-login-loop">
            {['Explore', 'Decode', 'Build', 'Belong'].map((step, index) => (
              <li key={step}>
                <span aria-hidden="true">0{index + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </div>
        <span className="player-login-world-status">
          <span aria-hidden="true" /> Living realm · progress held securely
        </span>
      </section>
      <StonePanel className="player-login-card" ornament={2}>
        <header className="player-login-card__header">
          <div aria-hidden="true" className="player-login-seal">
            <span />
          </div>
          <div>
            <span className="player-login-eyebrow">Return to the lake</span>
            <h1 id="player-login-title">Continue your path</h1>
          </div>
        </header>
        <p className="player-login-intro">
          Sign in with the account that owns this realm. Your settlement and Codex are waiting.
        </p>
        {notice && (
          <div className="player-login-notice" role="status">
            <span aria-hidden="true">⌛</span>
            <p>{notice}</p>
          </div>
        )}
        <form className="player-login-form" onSubmit={submit}>
          <div className="player-login-fields">
            <div className="player-login-field">
              <label htmlFor={emailId}>Player email</label>
              <div className="player-login-input">
                <span aria-hidden="true" className="player-login-input__icon">
                  <svg focusable="false" viewBox="0 0 24 24">
                    <path d="M3.5 6.5h17v12h-17zM4 7l8 6 8-6" />
                  </svg>
                </span>
                <input
                  aria-describedby={login.error ? errorId : undefined}
                  aria-invalid={Boolean(login.error)}
                  autoCapitalize="none"
                  autoComplete="username"
                  disabled={login.isPending}
                  id={emailId}
                  onChange={(event) => {
                    setEmail(event.currentTarget.value);
                    clearError();
                  }}
                  placeholder="wayfinder@example.com"
                  required
                  spellCheck={false}
                  type="email"
                  value={email}
                />
              </div>
            </div>
            <div className="player-login-field">
              <label htmlFor={passwordId}>Password</label>
              <div className="player-login-input">
                <span aria-hidden="true" className="player-login-input__icon">
                  <svg focusable="false" viewBox="0 0 24 24">
                    <rect height="10" rx="2" width="15" x="4.5" y="10" />
                    <path d="M8 10V7.5a4 4 0 0 1 8 0V10M12 14v2" />
                  </svg>
                </span>
                <input
                  aria-describedby={login.error ? errorId : undefined}
                  aria-invalid={Boolean(login.error)}
                  autoComplete="current-password"
                  disabled={login.isPending}
                  id={passwordId}
                  minLength={12}
                  onChange={(event) => {
                    setPassword(event.currentTarget.value);
                    clearError();
                  }}
                  placeholder="Your password"
                  required
                  type={passwordVisible ? 'text' : 'password'}
                  value={password}
                />
                <button
                  aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                  aria-pressed={passwordVisible}
                  className="player-login-password-toggle"
                  disabled={login.isPending}
                  onClick={() => setPasswordVisible((visible) => !visible)}
                  type="button"
                >
                  {passwordVisible ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
          </div>
          {login.error && (
            <div className="player-login-error" id={errorId} role="alert">
              <span aria-hidden="true">!</span>
              <p>
                <strong>The path did not open.</strong>
                {login.error instanceof Error ? login.error.message : 'Sign-in failed.'}
              </p>
            </div>
          )}
          <PrimaryActionButton loading={login.isPending} type="submit">
            {login.isPending ? 'Opening your realm…' : 'Enter the realm'}
          </PrimaryActionButton>
          <nav aria-label="Account access" className="player-login-actions">
            <a href="/register">Create account</a>
            <a href="/forgot-password">Forgot password?</a>
            <a href="/resend-verification">Resend verification</a>
          </nav>
        </form>
        <div className="player-login-security">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
            <path d="M12 3 5 6v5c0 4.4 2.8 8.3 7 10 4.2-1.7 7-5.6 7-10V6l-7-3Z" />
            <path d="m9 12 2 2 4-5" />
          </svg>
          <p>
            <strong>Private by design</strong>
            Secure session · no password stored in this browser
          </p>
        </div>
        <p aria-label={`Application build ${appBuildLabel}`} className="player-login-build">
          {appBuildLabel}
        </p>
      </StonePanel>
    </main>
  );
}
